import { isApiSessionExpired, readSaTokenHeader } from '@glyphic/shared-core'

import { notifyApiSessionExpired } from '../api/authInterceptors'
import { readApiBase, readAppOrigin } from '../api/config'
import type { ReferenceImageUrlHints } from './referenceImageUrl'
import { resolveMediaImage } from './resolvedMediaImage'
import { getAuthToken } from './session'

function appOriginsForCors(): string[] {
  const out = new Set<string>()
  try {
    if (typeof window !== 'undefined' && window.location?.origin) {
      out.add(window.location.origin)
    }
  } catch {
    /* ignore */
  }
  try {
    const app = readAppOrigin().replace(/\/+$/, '')
    if (app) out.add(new URL(app).origin)
  } catch {
    /* ignore */
  }
  try {
    const api = readApiBase().replace(/\/+$/, '')
    const base = /^https?:\/\//i.test(api)
      ? api
      : typeof window !== 'undefined' && window.location?.origin
        ? `${window.location.origin}${api.startsWith('/') ? '' : '/'}${api}`
        : api
    if (base) out.add(new URL(base).origin)
  } catch {
    /* ignore */
  }
  return [...out]
}

/**
 * 与「存入图库」拉取二进制一致：同源/同应用根可带 Cookie；外链 CDN 必须用 omit，否则常见 CORS 整段失败。
 */
export function imageUrlShouldIncludeCredentials(rawUrl: string): boolean {
  const s = rawUrl.trim()
  if (!s || s.startsWith('blob:') || s.startsWith('data:')) return true
  try {
    const currentScheme = typeof window !== 'undefined' && window.location.protocol === 'http:' ? 'http:' : 'https:'
    const u = new URL(s.startsWith('//') ? `${currentScheme}${s}` : s)
    const origins = appOriginsForCors()
    return origins.some((o) => o === u.origin)
  } catch {
    return false
  }
}

async function readProxyErrorMessage(res: Response): Promise<string> {
  const ct = (res.headers.get('content-type') ?? '').toLowerCase()
  if (ct.includes('json')) {
    try {
      const body = (await res.json()) as { code?: string; message?: string }
      if (isApiSessionExpired(body.code, res.status)) {
        notifyApiSessionExpired()
      }
      if (body.message?.trim()) return body.message.trim()
    } catch {
      /* ignore */
    }
  } else if (isApiSessionExpired(undefined, res.status)) {
    notifyApiSessionExpired()
  }
  return `服务端拉取图片失败（${res.status}）`
}

/** 登录用户经 API 代拉图片，绕过外链 CORS（对应 GET /v1/uploads/image-bytes） */
async function fetchImageBlobViaServerProxy(ingestUrl: string): Promise<Blob> {
  const displayUrl = ingestUrl.trim()
  const token = getAuthToken()
  if (!token) {
    throw new Error('请先登录后再复制或保存外链图片')
  }
  const base = readApiBase().replace(/\/+$/, '')
  const headerName = readSaTokenHeader(
    () => import.meta.env.VITE_SA_TOKEN_HEADER as string | undefined,
    { dev: import.meta.env.DEV },
  )
  const headers = new Headers({ Accept: 'image/*,application/octet-stream' })
  headers.set(headerName, token)
  const q = new URLSearchParams({ url: displayUrl })
  let res: Response
  try {
    res = await fetch(`${base}/v1/uploads/image-bytes?${q}`, {
      credentials: 'include',
      headers,
      cache: 'no-store',
    })
  } catch {
    throw new Error('无法连接服务端代拉接口，请确认后端已启动并已登录')
  }
  if (!res.ok) {
    throw new Error(await readProxyErrorMessage(res))
  }
  const blob = await res.blob()
  if (!blob.size) {
    throw new Error('服务端返回的图片为空')
  }
  const blobType = blob.type.toLowerCase()
  if (blobType.includes('json') || blobType.includes('html')) {
    throw new Error('服务端未返回有效图片，请检查图片地址是否可访问')
  }
  return blob
}

async function fetchImageBlobDirect(normalized: string): Promise<Blob> {
  if (normalized.startsWith('blob:') || normalized.startsWith('data:')) {
    const res = await fetch(normalized)
    if (!res.ok) {
      throw new Error(`无法读取图片（${res.status}）`)
    }
    return res.blob()
  }
  const credentials = imageUrlShouldIncludeCredentials(normalized) ? ('include' as RequestCredentials) : 'omit'
  const res = await fetch(normalized, {
    mode: 'cors',
    credentials,
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
  })
  if (!res.ok) {
    throw new Error(`无法读取图片（${res.status}），可能被跨域策略拦截`)
  }
  return res.blob()
}

function mergeFetchErrors(directErr: unknown, proxyErr: unknown): Error {
  const proxyMsg = proxyErr instanceof Error ? proxyErr.message : String(proxyErr)
  if (proxyMsg && !/failed to fetch/i.test(proxyMsg)) {
    return new Error(proxyMsg)
  }
  const directMsg = directErr instanceof Error ? directErr.message : String(directErr)
  if (directMsg && !/failed to fetch/i.test(directMsg)) {
    return new Error(directMsg)
  }
  return new Error('无法拉取图片，请尝试「复制图片链接」或先保存图片')
}

/**
 * 拉取远程图片为 Blob。
 * - 外链 CDN：已登录时优先服务端代拉（避免浏览器 CORS）
 * - 本站 /uploads：浏览器直连（可走 Cookie / Vite 代理）
 */
export async function fetchRemoteImageBlob(
  src: string,
  hints?: ReferenceImageUrlHints,
): Promise<Blob> {
  const media = resolveMediaImage(src, hints)
  if (!media.displayUrl) {
    throw new Error('图片地址为空')
  }

  const hasToken = Boolean(getAuthToken())

  if (media.kind === 'remote-cdn' && hasToken) {
    try {
      return await fetchImageBlobViaServerProxy(media.ingestUrl)
    } catch (proxyErr) {
      try {
        return await fetchImageBlobDirect(media.displayUrl)
      } catch (directErr) {
        throw mergeFetchErrors(directErr, proxyErr)
      }
    }
  }

  try {
    return await fetchImageBlobDirect(media.displayUrl)
  } catch (directErr) {
    if (!hasToken) {
      throw mergeFetchErrors(directErr, null)
    }
    try {
      return await fetchImageBlobViaServerProxy(media.ingestUrl)
    } catch (proxyErr) {
      throw mergeFetchErrors(directErr, proxyErr)
    }
  }
}

export function extensionForImageMime(mime: string): string {
  const m = mime.toLowerCase()
  if (m.includes('png')) return 'png'
  if (m.includes('webp')) return 'webp'
  if (m.includes('gif')) return 'gif'
  if (m.includes('jpeg') || m.includes('jpg')) return 'jpg'
  return 'png'
}

export async function imageBlobToFile(blob: Blob, baseName: string): Promise<File> {
  const mime = blob.type?.startsWith('image/') ? blob.type : 'image/png'
  const ext = extensionForImageMime(mime)
  const stem = baseName.trim().slice(0, 80).replace(/[/\\]/g, '_') || `image_${Date.now()}`
  const filename = `${stem}.${ext}`
  return new File([blob], filename, { type: mime })
}
